import React, { useState, useRef, useEffect } from "react";
import { Button } from "../../components/Button/Button";
import { Input } from "../../components/Input/Input";
import { SectionText } from "../../components/SectionText/SectionText";
import { Form } from "../../templates/Form/Form";
import { PageTemplate } from "../../templates/PageTemplate/PageTemplate";
import { LinkNormativa } from "../../components/LinkNormativa/LinkNormativa";
import { removeFieldError } from "../../helpers/error";
import { checkEmptyField, setValidationAndErrors, setValidationValue } from "../../helpers/validationFields";
import { getFieldsFromForm } from "../../helpers/form";
import { useApi } from "../../hooks/useApi";
import { Simulation } from "../../hooks/simulationData";
import { Loader } from "../../templates/Loader/Loader";
import { useDynatrace } from "../../hooks/useDynatrace";

export const Page = ({ onNextRoute }) => {
    const { checkUserData, getFinalStepPayload, finalStep, disaster, pingSapient } = useApi();

    const [isButtonEnabled, setIsButtonEnabled] = useState(null != Simulation.get("tel") && null != Simulation.get("cf"));
    const [cellulare, setCellulare] = useState(null != Simulation.get("tel") ? Simulation.get("tel") : "");
    const [codiceFiscale, setCodiceFiscale] = useState(null != Simulation.get("cf") ? Simulation.get("cf") : "");

    const [isBusy, setIsBusy] = useState(false);
    const [cfLength, setCfLength] = useState(0);

    const cellulareRef = useRef();
    const codiceFiscaleRef = useRef();
    const formRef = useRef();

    const {} = useDynatrace();

    const goToBizmaticaForm = () => {
        Simulation.set("EsitoOTP", "");

        pingSapient().then((res) => {
            if (res.data && res.data.status && res.data.status == "KO") {
                disaster("ping a macchine ha risposto KO", 200, "{}");
                return;
            }

            finalStep({
                SC_TOKEN: Simulation.get("resToken"),
            })
                .then((res) => {
                    if (res.success == true) {
                        window.onbeforeunload = null;
                        window.location.href = res.redirectUrl;
                    } else {
                        setIsBusy(false);
                        setIsButtonEnabled(true);
                    }
                })
                .catch((err) => {
                    disaster(
                        `chiamata a https://ds-richiesta.compass.it/main/api/init fallita: ${err ? err.message || err.toString() : "nessun messaggio di errore"}`,
                        500,
                        JSON.stringify({ ...getFinalStepPayload(), SC_TOKEN: Simulation.get("resToken") })
                    );
                });
        });
    };

    const handleInputTextChange = (event) => {
        const target = event.target;
        const value = target.value;

        setCellulare(value);

        const isEmpty = checkEmptyField(target, value);

        if (isEmpty) {
            removeFieldError(target);
        }

        toggleSubmitButton();
    };

    const handleCfInputTextChange = (event) => {
        const target = event.target;
        const value = target.value;

        setCodiceFiscale(value);

        // check if input field is empty
        const isEmpty = checkEmptyField(target, value);

        // then set state for button
        if (isEmpty) {
            removeFieldError(target);
        }

        // feedback
        setCfLength(value.length);

        toggleSubmitButton();
    };

    const toggleSubmitButton = () => {
        const cfNode = document.querySelector("#codFiscale");
        const phoneNode = document.querySelector("#phoneNumber");

        const isCfEmpty = checkEmptyField(cfNode, cfNode.value);
        const isPhoneEmpty = checkEmptyField(phoneNode, phoneNode.value);

        setIsButtonEnabled(!isCfEmpty && !isPhoneEmpty);
    };

    const handleFormSubmit = (event) => {
        setIsBusy(true);
        event.preventDefault();

        let isFormValid = 1;

        const inputs = getFieldsFromForm(formRef);
        inputs.forEach((item) => {
            const dataValidate = item.dataset.validate;
            let value = "";
            let dlError = "";
            if (dataValidate == "isCodFiscale") {
                value = item.value.replaceAll(" ", "");
            } else {
                value = item.value.replaceAll(" ", "").replaceAll("+", "00");
            }
            const isValid = setValidationAndErrors(item, value, dataValidate);

            if (!isValid) {
                if (dataValidate == "isCodFiscale") {
                    dlError = "Codice Fiscale";
                } else {
                    dlError = "Cellulare";
                }
                isFormValid *= 0;

                window.dataLayer.push({
                    event: "funnel_ds_errore",
                    eventCategory: "richiestaPrestito",
                    eventAction: "Errore",
                    eventLabel: `Codice Fiscale - Errore generico`,
                });
            }

            switch (dataValidate) {
                case "isPhoneNumber":
                    setValidationValue(isValid, cellulareRef, value);
                    break;
                case "isCodFiscale":
                    setValidationValue(isValid, codiceFiscaleRef, value);
                    break;
                default:
                    break;
            }
        });

        if (0 == isFormValid) return;

        if (cellulareRef.current !== undefined) {
            Simulation.set("tel", cellulareRef.current);
        }
        if (codiceFiscaleRef.current !== undefined) {
            Simulation.set("cf", codiceFiscaleRef.current);
        }

        const payload = {
            cf: Simulation.get("cf").toUpperCase(),
            tel: Simulation.get("tel"),
            SC_BENE: Simulation.get("purpose"),
            SC_BENE_DESCR: Simulation.get("purposeTitle"),
        };
        if (Simulation.get("token")) {
            payload["token"] = Simulation.get("token");
        }

        setIsButtonEnabled(false);
        checkUserData(payload)
            .then((res) => {
                const { NeedOTP, TokenOTP, Token, Result, Message } = res;

                Simulation.set("resToken", Token);
                Simulation.set("tokenOTP", TokenOTP);

                if (NeedOTP) {
                    Simulation.set("EsitoOTP", "N");
                    setIsButtonEnabled(true);
                    setIsBusy(false);
                    onNextRoute();
                } else {
                    goToBizmaticaForm();
                }
            })
            .catch((err) => {
                goToBizmaticaForm();
            });
    };

    useEffect(() => {
        window.dataLayer.push({
            event: "customPageview",
            fase_ds: "0 - simulatore",
            prestito: {
                importoRichiesto: `${Simulation.get("importo")}`,
                importoRata: `${Simulation.get("minRata")}/${Simulation.get("maxRata")}`,
                numeroRate: `${Simulation.get("minDurata")}/${Simulation.get("maxDurata")}`,
                obiettivo: Simulation.get("purposeTitle"),
            },
        });
    }, []);

    return (
        <PageTemplate headerCustomClasses={`${isBusy ? "no-back" : ""}`} footerCustomClasses={`${isBusy ? "hidden" : ""}`} modal={true} modalId="pre" customClasses={`${isBusy ? "--bg-yellow" : ""}`}>
            {isBusy && <Loader />}
            {!isBusy && (
                <main className="d-flex flex-column align-center dsk_text-center">
                    <div className="container --medium mBottom4x">
                        <Form customClassName="singleField" onSubmit={(event) => handleFormSubmit(event)} forwardedFormRef={formRef}>
                            <SectionText
                                hideText="no"
                                titleLabel="Inserisci il tuo numero di telefono e il tuo codice fiscale"
                                textLabel="Te lo chiediamo perché così possiamo sapere se sei già nostro Cliente, questo ci permetterà di inviarti il codice di sicurezza direttamente sul tuo cellulare."
                                customClasses="mBottom5x"
                            />

                            <Input
                                customClasses="--center mBottom2x"
                                label="Cellulare"
                                inputType="isPhoneNumber"
                                name="phoneNumber"
                                maxLength="15"
                                required
                                error="Inserisci un numero di cellulare"
                                value={cellulare}
                                onChange={(event) => handleInputTextChange(event)}
                            />

                            <Input
                                customClasses="--center"
                                label="Codice Fiscale"
                                inputType="isCodFiscale"
                                name="codFiscale"
                                error="Il codice inserito non è corretto"
                                required
                                value={codiceFiscale}
                                maxLength={16}
                                inputCustomClasses="text-uppercase"
                                onChange={(event) => handleCfInputTextChange(event)}
                            />

                            {cfLength > 0 && cfLength < 16 && (
                                <div className="text-muted text-left mTop1x text-small">
                                    &nbsp;Ancora {16 - cfLength} {cfLength == 15 ? "carattere disponibile" : "caratteri disponibili"}
                                </div>
                            )}

                            <SectionText
                                hideText="yes"
                                textLabel="Prendi visione dell'informativa resa da Compass ai sensi degli artt. 13 e 14 del Regolamento UE 2016/679 e della normativa nazionale vigente in materia di protezione dei dati personali qui scaricabile."
                                customClasses="mTop12x"
                            />

                            <Button
                                containerId="continue-btn"
                                color={isButtonEnabled ? "red" : "inactive"}
                                label="continua"
                                uppercase="yes"
                                positionIcon="right"
                                withIcon="yes"
                                customClasses={`btn-mobile-fixed --animated-arrow ${isBusy ? "busy" : ""}`}
                                mode={isButtonEnabled ? "active" : "inactive"}
                            />
                        </Form>
                    </div>

                    <div className="container --medium mTopAuto">
                        <LinkNormativa />
                    </div>
                </main>
            )}
        </PageTemplate>
    );
};
